import React, { useMemo, useState } from 'react';
import { OneWayInOpportunitySimpleV2Dto, OneWayInStatus } from 'src/api/v2';
import useOneWayInMutations from '../../hooks/use-one-way-in-mutations';
import createOwiUpdateDto from '../../opportunity-details/utils/createOwiUpdateDto';
import { useUser } from 'src/features/authorization';
import { useTranslation } from 'react-i18next';
import AddAdvisorModal from '../../opportunity-details/components/AddAdvisorModal/AddAdvisorModal';
import { useQueryClient } from '@tanstack/react-query';
import { ONE_WAY_IN_ALL_SAVED_FORMS_KEY } from 'src/constants/hook-keys';
import OpportunityColumn from './OpportunityColumn';
import RecommendServicesModal from './RecommendServicesModal';
import DeliverServicesModal from './DeliverServicesModal';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';

type Props = {
  opportunityList: OneWayInOpportunitySimpleV2Dto[];
};

//Statusmeldingene er på norsk inntil videre, lager en liten oversettelsesblokk her
export const statusTranslations = Object.freeze({
  [OneWayInStatus.New]: 'Ny',
  [OneWayInStatus.Active]: 'I arbeid',
  [OneWayInStatus.Closed]: 'Arkivert',
  [OneWayInStatus.FollowUpLater]: 'På vent',
});

const OpportunityBoard = ({ opportunityList }: Props) => {
  const { updateOneWayInOpportunityStatus } = useOneWayInMutations();
  const { t: tOneWayIn } = useTranslation('oneWayIn');

  const { user, isAadUser, displayName } = useUser();
  const queryClient = useQueryClient();

  const [selectedOpportunity, setSelectedOpportunity] = useState<OneWayInOpportunitySimpleV2Dto | undefined>(
    undefined,
  );
  const [showAdvisorDialog, setShowAdvisorDialog] = useState(false);
  const [showRecommendServicesDialog, setShowRecommendServicesDialog] = useState(false);
  const [showDeliverServicesDialog, setShowDeliverServicesDialog] = useState(false);
  const siteimprove = useSiteimprove();

  const filteredOpportunities = useMemo(() => {
    const archivedStatuses = [OneWayInStatus.Closed, OneWayInStatus.Forwarded];
    const newStatuses = [OneWayInStatus.New, OneWayInStatus.Opened, OneWayInStatus.Received];

    if (!opportunityList) {
      return {
        newOppoortunities: [],
        wipOpportunities: [],
        onHoldOpportunities: [],
        archivedOpportunities: [],
      };
    }

    const opportunities = opportunityList.reduce(
      (acc, opportunity) => {
        if (
          opportunity.status?.status === undefined ||
          (!archivedStatuses.includes(opportunity.status.status) &&
            !!newStatuses.includes(opportunity.status.status))
        ) {
          acc.newOppoortunities.push(opportunity);
        } else if (
          opportunity.status.status === OneWayInStatus.Active ||
          opportunity.status.status === OneWayInStatus.EmailReceivedFromCustomer ||
          opportunity.status.status === OneWayInStatus.EmailSentToCustomer ||
          opportunity.status.status === OneWayInStatus.ReOpened
        ) {
          acc.wipOpportunities.push(opportunity);
        } else if (opportunity.status.status === OneWayInStatus.FollowUpLater) {
          acc.onHoldOpportunities.push(opportunity);
        } else {
          acc.archivedOpportunities.push(opportunity);
        }
        return acc;
      },
      {
        newOppoortunities: [] as OneWayInOpportunitySimpleV2Dto[],
        wipOpportunities: [] as OneWayInOpportunitySimpleV2Dto[],
        onHoldOpportunities: [] as OneWayInOpportunitySimpleV2Dto[],
        archivedOpportunities: [] as OneWayInOpportunitySimpleV2Dto[],
      },
    );
    return opportunities;
  }, [opportunityList]);

  async function onChangeStatus(id: string, status: OneWayInStatus) {
    const opportunity = opportunityList?.find((oppo) => oppo.id === id);
    if (opportunity?.status?.status === status) return;
    queryClient.cancelQueries([ONE_WAY_IN_ALL_SAVED_FORMS_KEY]);
    queryClient.setQueryData(
      [ONE_WAY_IN_ALL_SAVED_FORMS_KEY],
      (oldData: OneWayInOpportunitySimpleV2Dto[] | undefined) => {
        if (!oldData) return [];
        const newData = [...oldData];

        const index = newData?.findIndex((form) => form.id === opportunity?.id);
        const oldStatus = opportunity?.status;
        if (index !== undefined && opportunity && oldStatus) {
          newData.splice(index, 1, { ...opportunity, status: { ...oldStatus, status: status } });

          return newData;
        }

        return newData;
      },
    );
    await updateOneWayInOpportunityStatus.mutateAsync({
      updateDto: createOwiUpdateDto(
        opportunity?.id,
        status,
        `Endret status til ${statusTranslations[status]} via tavlevisning`,
        isAadUser ? undefined : user?.clusterUserId,
        displayName,
        undefined,
      ),
    });
  }

  const onAddAdvisorModalOpen = (opportunity: OneWayInOpportunitySimpleV2Dto) => {
    setSelectedOpportunity(opportunity);
    setShowAdvisorDialog(true);
    siteimprove.log(
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.OPEN_ASSIGN_ADVISOR_MODAL,
    );
  };

  const onAddAdvisorModalClose = () => {
    setSelectedOpportunity(undefined);
    setShowAdvisorDialog(false);
  };

  const onRecommendServicesModalOpen = (opportunity: OneWayInOpportunitySimpleV2Dto) => {
    setSelectedOpportunity(opportunity);
    setShowRecommendServicesDialog(true);
    siteimprove.log(
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.OPEN_RECOMMEND_SERVICES_MODAL,
    );
  };

  const onRecommendServicesModalClose = () => {
    setSelectedOpportunity(undefined);
    setShowRecommendServicesDialog(false);
  };

  const onDeliverServicesModalOpen = (opportunity: OneWayInOpportunitySimpleV2Dto) => {
    setSelectedOpportunity(opportunity);
    setShowDeliverServicesDialog(true);
    siteimprove.log(
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.id,
      SITEIMPROVE_DECLARATIONS.OPPORTUNITY_BOARD.actions.OPEN_DELIVER_SERVICES_MODAL,
    );
  };

  const onDeliverServicesModalClose = () => {
    setSelectedOpportunity(undefined);
    setShowDeliverServicesDialog(false);
  };

  return (
    <div id="oppo__board" className="board">
      <OpportunityColumn
        opportunityList={filteredOpportunities.newOppoortunities}
        name={`${tOneWayIn('status.New')}/${tOneWayIn('status.Opened')}`}
        onChangeStatus={onChangeStatus}
        onAddAdvisorModalOpen={onAddAdvisorModalOpen}
        color="blue"
        statusLegend={
          <div className="flex gap--1 col__name">
            <div className={`dot blue`}></div>
            <div className={``}>{tOneWayIn('status.New')}</div>|<div className={`dot blue outline`}></div>
            <div className={``}>{tOneWayIn('status.Opened')}</div>
          </div>
        }
        onRecommendServicesModalOpen={onRecommendServicesModalOpen}
        onDeliverServicesModalOpen={onDeliverServicesModalOpen}
      />
      <OpportunityColumn
        opportunityList={filteredOpportunities.wipOpportunities}
        status={OneWayInStatus.Active}
        name={tOneWayIn('status.wip')}
        onChangeStatus={onChangeStatus}
        onAddAdvisorModalOpen={onAddAdvisorModalOpen}
        color="green"
        onRecommendServicesModalOpen={onRecommendServicesModalOpen}
        onDeliverServicesModalOpen={onDeliverServicesModalOpen}
      />
      <OpportunityColumn
        opportunityList={filteredOpportunities.onHoldOpportunities}
        status={OneWayInStatus.FollowUpLater}
        name={tOneWayIn('status.FollowUpLater')}
        onChangeStatus={onChangeStatus}
        onAddAdvisorModalOpen={onAddAdvisorModalOpen}
        color="orange"
        onRecommendServicesModalOpen={onRecommendServicesModalOpen}
        onDeliverServicesModalOpen={onDeliverServicesModalOpen}
      />
      <OpportunityColumn
        opportunityList={filteredOpportunities.archivedOpportunities}
        status={OneWayInStatus.Closed}
        minimizable
        name={tOneWayIn('status.Archived')}
        onChangeStatus={onChangeStatus}
        onAddAdvisorModalOpen={onAddAdvisorModalOpen}
        color="gray"
        onRecommendServicesModalOpen={onRecommendServicesModalOpen}
        onDeliverServicesModalOpen={onDeliverServicesModalOpen}
      />
      {selectedOpportunity && (
        <AddAdvisorModal
          show={showAdvisorDialog}
          onClose={onAddAdvisorModalClose}
          opportunity={selectedOpportunity}
        />
      )}
      <RecommendServicesModal
        dialogOpen={showRecommendServicesDialog}
        onClose={onRecommendServicesModalClose}
        opportunities={selectedOpportunity ? [selectedOpportunity] : []}
      />
      <DeliverServicesModal
        dialogOpen={showDeliverServicesDialog}
        onClose={onDeliverServicesModalClose}
        opportunities={selectedOpportunity ? [selectedOpportunity] : []}
      />
    </div>
  );
};

export default OpportunityBoard;
